import React from 'react';
import { Sparkles, Check, ExternalLink } from 'lucide-react';

/**
 * CapabilitySuggestion - Displays AI-driven vendor/product suggestions
 * for a Zero Trust capability based on procurement history.
 *
 * Shows:
 * - Suggested vendor and product with confidence badge
 * - Transaction count indicator
 * - "Accept" button to auto-fill the current solution field
 * - "View Transactions" link to open the transaction modal
 *
 * @param {Object} props
 * @param {Object} props.suggestion - Suggestion data from useSuggestions
 * @param {function} props.onAccept - Callback when user clicks Accept (vendor, product)
 * @param {function} props.onViewTransactions - Callback to open transaction modal
 */
const CapabilitySuggestion = ({ suggestion, onAccept, onViewTransactions }) => {
  if (!suggestion || !suggestion.vendor) {
    return null;
  }

  const { vendor, product, confidence, totalSpend, productCount } = suggestion;

  // Determine confidence level styling
  const getConfidenceColor = (conf) => {
    if (conf >= 80) return 'var(--zt-strong)';
    if (conf >= 50) return 'var(--zt-advanced)';
    return 'var(--zt-partial)';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${Math.round(amount)}`;
  };

  const transactionCount = productCount || suggestion.detected?.length || 0;

  return (
    <div className="capability-suggestion">
      <div className="capability-suggestion-header">
        <Sparkles size={14} className="capability-suggestion-icon" />
        <span className="capability-suggestion-label">AI Suggestion</span>
        <span
          className="capability-suggestion-confidence"
          style={{ color: getConfidenceColor(confidence) }}
        >
          {confidence}% match
        </span>
      </div>

      <div className="capability-suggestion-content">
        <div className="capability-suggestion-vendor">
          <span className="suggestion-vendor-name">{vendor}</span>
          {product && (
            <span className="suggestion-product-name">{product}</span>
          )}
        </div>

        <div className="capability-suggestion-meta">
          {transactionCount > 0 && (
            <span className="suggestion-transaction-count">
              Based on {transactionCount} transaction{transactionCount !== 1 ? 's' : ''}
            </span>
          )}
          {totalSpend > 0 && (
            <span className="suggestion-spend">
              {formatCurrency(totalSpend)} spend
            </span>
          )}
        </div>
      </div>

      <div className="capability-suggestion-actions">
        <button
          className="suggestion-accept-btn"
          onClick={() => onAccept && onAccept(vendor, product)}
          title={`Auto-fill with ${vendor}${product ? ' ' + product : ''}`}
        >
          <Check size={12} />
          Accept
        </button>
        <button
          className="suggestion-view-btn"
          onClick={() => onViewTransactions && onViewTransactions()}
          title="View supporting transactions"
        >
          <ExternalLink size={12} />
          View Transactions
        </button>
      </div>
    </div>
  );
};

export default CapabilitySuggestion;
